import type { TranslationTable } from "@repo/shared/types";

const EN_GB = {
  userDashboard: "User Dashboard",
  performanceDashboard: "Performance Dashboard",
  dashboardSelect: "Dashboard",
  tenantDashboard: "Tenant Dashboard",
  userDashboards: "User Dashboards",
  me: "Me",
  tenantWide: "Tenant-wide",
  clientCount: (n: string) => `${n} clients`,
  clientCountOne: "1 client",
  userCount: (n: string) => `${n} users`,
  userCountOne: "1 user",
};

// All 17 locales (QL-17 plan keeps them); the type makes each one match EN_GB.
export const T: TranslationTable<typeof EN_GB> = {
  en_GB: EN_GB,
  fr_FR: {
    userDashboard: "Tableau de bord utilisateur",
    performanceDashboard: "Tableau de bord des performances",
    dashboardSelect: "Tableau de bord",
    tenantDashboard: "Tableau de bord de l'organisation",
    userDashboards: "Tableaux de bord utilisateurs",
    me: "Moi",
    tenantWide: "Toute l'organisation",
    clientCount: (n) => `${n} clients`,
    clientCountOne: "1 client",
    userCount: (n) => `${n} utilisateurs`,
    userCountOne: "1 utilisateur",
  },
  es_ES: {
    userDashboard: "Panel de usuario",
    performanceDashboard: "Panel de rendimiento",
    dashboardSelect: "Panel",
    tenantDashboard: "Panel de la organización",
    userDashboards: "Paneles de usuario",
    me: "Yo",
    tenantWide: "Toda la organización",
    clientCount: (n) => `${n} clientes`,
    clientCountOne: "1 cliente",
    userCount: (n) => `${n} usuarios`,
    userCountOne: "1 usuario",
  },
  pt_BR: {
    userDashboard: "Painel do usuário",
    performanceDashboard: "Painel de desempenho",
    dashboardSelect: "Painel",
    tenantDashboard: "Painel da organização",
    userDashboards: "Painéis de usuários",
    me: "Eu",
    tenantWide: "Toda a organização",
    clientCount: (n) => `${n} clientes`,
    clientCountOne: "1 cliente",
    userCount: (n) => `${n} usuários`,
    userCountOne: "1 usuário",
  },
  de_DE: {
    userDashboard: "Benutzer-Dashboard",
    performanceDashboard: "Performance-Dashboard",
    dashboardSelect: "Dashboard",
    tenantDashboard: "Organisations-Dashboard",
    userDashboards: "Benutzer-Dashboards",
    me: "Ich",
    tenantWide: "Gesamte Organisation",
    clientCount: (n) => `${n} Kunden`,
    clientCountOne: "1 Kunde",
    userCount: (n) => `${n} Benutzer`,
    userCountOne: "1 Benutzer",
  },
  it_IT: {
    userDashboard: "Dashboard utente",
    performanceDashboard: "Dashboard delle performance",
    dashboardSelect: "Dashboard",
    tenantDashboard: "Dashboard dell'organizzazione",
    userDashboards: "Dashboard utenti",
    me: "Io",
    tenantWide: "Tutta l'organizzazione",
    clientCount: (n) => `${n} clienti`,
    clientCountOne: "1 cliente",
    userCount: (n) => `${n} utenti`,
    userCountOne: "1 utente",
  },
  ar: {
    userDashboard: "لوحة معلومات المستخدم",
    performanceDashboard: "لوحة معلومات الأداء",
    dashboardSelect: "لوحة المعلومات",
    tenantDashboard: "لوحة معلومات المؤسسة",
    userDashboards: "لوحات معلومات المستخدمين",
    me: "أنا",
    tenantWide: "المؤسسة بالكامل",
    clientCount: (n) => `${n} عملاء`,
    clientCountOne: "عميل واحد",
    userCount: (n) => `${n} مستخدمين`,
    userCountOne: "مستخدم واحد",
  },
  ja_JP: {
    userDashboard: "ユーザーダッシュボード",
    performanceDashboard: "パフォーマンスダッシュボード",
    dashboardSelect: "ダッシュボード",
    tenantDashboard: "組織ダッシュボード",
    userDashboards: "ユーザーダッシュボード",
    me: "自分",
    tenantWide: "組織全体",
    clientCount: (n) => `顧客 ${n} 名`,
    clientCountOne: "顧客 1 名",
    userCount: (n) => `ユーザー ${n} 名`,
    userCountOne: "ユーザー 1 名",
  },
  hi_IN: {
    userDashboard: "उपयोगकर्ता डैशबोर्ड",
    performanceDashboard: "प्रदर्शन डैशबोर्ड",
    dashboardSelect: "डैशबोर्ड",
    tenantDashboard: "संगठन डैशबोर्ड",
    userDashboards: "उपयोगकर्ता डैशबोर्ड",
    me: "मैं",
    tenantWide: "पूरा संगठन",
    clientCount: (n) => `${n} क्लाइंट`,
    clientCountOne: "1 क्लाइंट",
    userCount: (n) => `${n} उपयोगकर्ता`,
    userCountOne: "1 उपयोगकर्ता",
  },
  zh_CN: {
    userDashboard: "用户仪表板",
    performanceDashboard: "业绩仪表板",
    dashboardSelect: "仪表板",
    tenantDashboard: "机构仪表板",
    userDashboards: "用户仪表板",
    me: "我",
    tenantWide: "全机构",
    clientCount: (n) => `${n} 位客户`,
    clientCountOne: "1 位客户",
    userCount: (n) => `${n} 位用户`,
    userCountOne: "1 位用户",
  },
  zh_TW: {
    userDashboard: "使用者儀表板",
    performanceDashboard: "績效儀表板",
    dashboardSelect: "儀表板",
    tenantDashboard: "機構儀表板",
    userDashboards: "使用者儀表板",
    me: "我",
    tenantWide: "全機構",
    clientCount: (n) => `${n} 位客戶`,
    clientCountOne: "1 位客戶",
    userCount: (n) => `${n} 位使用者`,
    userCountOne: "1 位使用者",
  },
  nl_NL: {
    userDashboard: "Gebruikersdashboard",
    performanceDashboard: "Prestatiedashboard",
    dashboardSelect: "Dashboard",
    tenantDashboard: "Organisatiedashboard",
    userDashboards: "Gebruikersdashboards",
    me: "Ik",
    tenantWide: "Hele organisatie",
    clientCount: (n) => `${n} klanten`,
    clientCountOne: "1 klant",
    userCount: (n) => `${n} gebruikers`,
    userCountOne: "1 gebruiker",
  },
  nb_NO: {
    userDashboard: "Brukerdashbord",
    performanceDashboard: "Resultatdashbord",
    dashboardSelect: "Dashbord",
    tenantDashboard: "Organisasjonsdashbord",
    userDashboards: "Brukerdashbord",
    me: "Meg",
    tenantWide: "Hele organisasjonen",
    clientCount: (n) => `${n} kunder`,
    clientCountOne: "1 kunde",
    userCount: (n) => `${n} brukere`,
    userCountOne: "1 bruker",
  },
  sv_SE: {
    userDashboard: "Användardashboard",
    performanceDashboard: "Resultatdashboard",
    dashboardSelect: "Dashboard",
    tenantDashboard: "Organisationsdashboard",
    userDashboards: "Användardashboards",
    me: "Jag",
    tenantWide: "Hela organisationen",
    clientCount: (n) => `${n} kunder`,
    clientCountOne: "1 kund",
    userCount: (n) => `${n} användare`,
    userCountOne: "1 användare",
  },
  fi_FI: {
    userDashboard: "Käyttäjän koontinäyttö",
    performanceDashboard: "Tuloskoontinäyttö",
    dashboardSelect: "Koontinäyttö",
    tenantDashboard: "Organisaation koontinäyttö",
    userDashboards: "Käyttäjien koontinäytöt",
    me: "Minä",
    tenantWide: "Koko organisaatio",
    clientCount: (n) => `${n} asiakasta`,
    clientCountOne: "1 asiakas",
    userCount: (n) => `${n} käyttäjää`,
    userCountOne: "1 käyttäjä",
  },
  da_DK: {
    userDashboard: "Brugerdashboard",
    performanceDashboard: "Resultatdashboard",
    dashboardSelect: "Dashboard",
    tenantDashboard: "Organisationsdashboard",
    userDashboards: "Brugerdashboards",
    me: "Mig",
    tenantWide: "Hele organisationen",
    clientCount: (n) => `${n} kunder`,
    clientCountOne: "1 kunde",
    userCount: (n) => `${n} brugere`,
    userCountOne: "1 bruger",
  },
  is_IS: {
    userDashboard: "Notendamælaborð",
    performanceDashboard: "Árangursmælaborð",
    dashboardSelect: "Mælaborð",
    tenantDashboard: "Mælaborð fyrirtækis",
    userDashboards: "Notendamælaborð",
    me: "Ég",
    tenantWide: "Allt fyrirtækið",
    clientCount: (n) => `${n} viðskiptavinir`,
    clientCountOne: "1 viðskiptavinur",
    userCount: (n) => `${n} notendur`,
    userCountOne: "1 notandi",
  },
};
